import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  VStack,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Badge,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import {
  getMFAStatus,
  generateBackupCodes,
  disableMFA,
} from '../../../queries';
import { MFASetupModal } from './MFASetupModal';
import { BackupCodesDisplay } from './BackupCodesDisplay';

const MFASettings = () => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    isOpen: isSetupOpen,
    onOpen: onSetupOpen,
    onClose: onSetupClose,
  } = useDisclosure();

  const {
    isOpen: isDisableOpen,
    onOpen: onDisableOpen,
    onClose: onDisableClose,
  } = useDisclosure();

  const {
    isOpen: isBackupCodesOpen,
    onOpen: onBackupCodesOpen,
    onClose: onBackupCodesClose,
  } = useDisclosure();

  const fetchMFAStatus = async () => {
    try {
      setIsLoading(true);
      const response = await getMFAStatus();
      setMfaStatus(response.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error('Failed to load MFA status');
    }
  };

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const handleSetupSuccess = () => {
    fetchMFAStatus();
    toast.success('Two-factor authentication enabled successfully!');
  };

  const handleGenerateBackupCodes = async () => {
    if (
      !window.confirm(
        'Generating new backup codes will invalidate all previous codes. Continue?'
      )
    ) {
      return;
    }

    try {
      setIsGenerating(true);
      const response = await generateBackupCodes();
      setBackupCodes(response.data.backupCodes);
      onBackupCodesOpen();
      setIsGenerating(false);
      toast.success('New backup codes generated');
    } catch (error) {
      setIsGenerating(false);
      toast.error(
        error.response?.data?.message || 'Failed to generate backup codes'
      );
    }
  };

  const handleDisableMFA = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setIsDisabling(true);
      await disableMFA(password);
      setMfaStatus({ ...mfaStatus, mfaEnabled: false });
      onDisableClose();
      setPassword('');
      setIsDisabling(false);
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      setIsDisabling(false);
      toast.error(error.response?.data?.message || 'Failed to disable MFA');
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Text>Loading...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="2rem">
        <Box>
          <HStack spacing="1rem">
            <Text fontSize="20px" fontFamily="bold" color="#444">
              Two-Factor Authentication
            </Text>
            {mfaStatus?.mfaEnabled && (
              <Badge
                colorScheme="green"
                fontSize="12px"
                px="0.75rem"
                py="0.25rem"
              >
                Enabled
              </Badge>
            )}
          </HStack>
          <Text fontSize="14px" color="#666" mt="0.5rem">
            Add an extra layer of security to your account
          </Text>
        </Box>
      </Flex>

      {!mfaStatus?.mfaEnabled ? (
        <Box bg="white" p="2rem" borderRadius="8px" border="1px solid #E0E0E0">
          <VStack align="start" spacing="1.5rem">
            <Box>
              <Text
                fontSize="16px"
                fontFamily="medium"
                color="#444"
                mb="0.5rem"
              >
                Protect Your Account
              </Text>
              <Text fontSize="14px" color="#666" lineHeight="1.6">
                Two-factor authentication (2FA) adds an extra layer of security
                to your account by requiring a code from your phone in addition
                to your password.
              </Text>
            </Box>

            <Box>
              <Text
                fontSize="14px"
                fontFamily="medium"
                color="#444"
                mb="0.5rem"
              >
                How it works:
              </Text>
              <VStack
                align="start"
                spacing="0.5rem"
                fontSize="14px"
                color="#666"
              >
                <Text>1. Scan a QR code with an authenticator app</Text>
                <Text>
                  2. Use the app to generate a 6-digit code when logging in
                </Text>
                <Text>3. Save backup codes for emergency access</Text>
              </VStack>
            </Box>

            <Button
              bg="#1A73E8"
              color="white"
              fontSize="14px"
              fontFamily="medium"
              h="45px"
              px="2rem"
              onClick={onSetupOpen}
              _hover={{ bg: '#1558D6' }}
            >
              Enable Two-Factor Authentication
            </Button>
          </VStack>
        </Box>
      ) : (
        <Box bg="white" p="2rem" borderRadius="8px" border="1px solid #E0E0E0">
          <VStack align="stretch" spacing="1.5rem">
            <Box
              p="1.5rem"
              bg="#E8F5E9"
              borderRadius="8px"
              border="1px solid #2EAE65"
            >
              <Text fontSize="14px" fontFamily="medium" color="#2EAE65">
                ✓ Two-factor authentication is active
              </Text>
              <Text fontSize="13px" color="#666" mt="0.5rem">
                Your account is protected with an additional layer of security.
              </Text>
            </Box>

            <HStack spacing="1rem">
              <Button
                bg="white"
                color="#1A73E8"
                border="1px solid #1A73E8"
                fontSize="14px"
                fontFamily="medium"
                h="45px"
                px="2rem"
                onClick={handleGenerateBackupCodes}
                isLoading={isGenerating}
                _hover={{ bg: '#EBF4FF' }}
              >
                Generate New Backup Codes
              </Button>

              <Button
                bg="white"
                color="#E24D4D"
                border="1px solid #E24D4D"
                fontSize="14px"
                fontFamily="medium"
                h="45px"
                px="2rem"
                onClick={onDisableOpen}
                _hover={{ bg: '#FFEBEE' }}
              >
                Disable 2FA
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      <MFASetupModal
        isOpen={isSetupOpen}
        onClose={onSetupClose}
        onSuccess={handleSetupSuccess}
      />

      <Modal isOpen={isDisableOpen} onClose={onDisableClose}>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
        <ModalContent maxW="500px" borderRadius="12px">
          <ModalHeader
            bg="#E24D4D"
            color="white"
            borderTopRadius="12px"
            py="1.5rem"
          >
            Disable Two-Factor Authentication
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py="2rem">
            <VStack align="stretch" spacing="1.5rem">
              <Box
                bg="#FFF3E0"
                border="1px solid #FFA726"
                borderRadius="8px"
                p="1.5rem"
              >
                <Text fontSize="14px" color="#E65100" fontFamily="medium">
                  ⚠️ Warning
                </Text>
                <Text fontSize="13px" color="#666" mt="0.5rem">
                  Disabling 2FA will make your account less secure. Only disable
                  if you understand the risks.
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="14px"
                  fontFamily="medium"
                  color="#444"
                  mb="0.5rem"
                >
                  Enter your password to confirm:
                </Text>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  h="45px"
                  borderColor="#E0E0E0"
                  _hover={{ borderColor: '#BDBDBD' }}
                  _focus={{
                    borderColor: '#1A73E8',
                    boxShadow: '0 0 0 1px #1A73E8',
                  }}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr="1rem"
              onClick={onDisableClose}
              fontSize="14px"
            >
              Cancel
            </Button>
            <Button
              bg="#E24D4D"
              color="white"
              fontSize="14px"
              onClick={handleDisableMFA}
              isLoading={isDisabling}
              _hover={{ bg: '#C62828' }}
            >
              Disable 2FA
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBackupCodesOpen} onClose={onBackupCodesClose} size="xl">
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
        <ModalContent maxW="600px" borderRadius="12px">
          <ModalHeader
            bg="#1A73E8"
            color="white"
            borderTopRadius="12px"
            py="1.5rem"
          >
            Your New Backup Codes
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py="2rem">
            <BackupCodesDisplay
              codes={backupCodes}
              onConfirm={onBackupCodesClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export { MFASettings };
